import { Capacitor } from '@capacitor/core';
import * as Sentry from '@sentry/capacitor';
import * as SentryReact from '@sentry/react';
import { rewriteFramesIntegration } from '@sentry/react';
import { ENV } from './ENV';
import { isProductionEnvironment } from './envUtils';

const isReleaseBuild = (): boolean => {
  // Web deployments to staging, etc. are considered "production" builds.
  // We need to target our production environment explicitly.
  if (!isProductionEnvironment()) {
    return false;
  }

  const isWebProductionBuild = !Capacitor.isNativePlatform() && ENV.PROD;
  const isNativeProductionBuild =
    Capacitor.isNativePlatform() && !Capacitor.DEBUG;

  return isWebProductionBuild || isNativeProductionBuild;
};

const getSentryReleaseName = (): string => {
  if (!isReleaseBuild()) {
    return 'pre-release-testing';
  }

  return ENV.VITE_SENTRY_RELEASE_VERSION;
};

const modifyStacktraceFilenames = (
  frame: Sentry.StackFrame,
): Sentry.StackFrame => {
  // This updates the stacktrace filenames to help point to our map files
  if (!frame.filename) {
    return frame;
  }
  // This prefix tells sentry parse the relative paths of our source mappings with this prefix + relative path
  // eslint-disable-next-line
  frame.filename = frame.filename.replace(/^.*\/assets\//, '/assets/');
  return frame;
};

export const initSentry = (): Promise<void> => {
  if (!ENV.VITE_SENTRY_DSN) {
    return Promise.resolve();
  }

  const release = getSentryReleaseName();

  const isNative = Capacitor.isNativePlatform();

  Sentry.init(
    {
      dsn: ENV.VITE_SENTRY_DSN,
      environment: ENV.VITE_SENTRY_ENV,
      release,
      // https://github.com/getsentry/sentry-capacitor/issues/63
      enableNative: isNative,
      integrations: [
        rewriteFramesIntegration({ iteratee: modifyStacktraceFilenames }),
      ],
      dist: '1', // we only vary release, not the dist. This matches the dist for source map bundles we upload,
      enableCaptureFailedRequests: false,
    },
    // Forward the init method from @sentry/react
    SentryReact.init,
  );
  Sentry.setTag('isNative', isNative);
  return Promise.resolve();
};
